import React, { useEffect, useState } from 'react'
import { Dimensions, Image, StyleSheet, View } from 'react-native'
import { useNavigation } from '@react-navigation/core'

import {
	createJuniorProfile,
	deleteJuniorProfile,
	getJuniorProfiles,
	updateJuniorProfile,
} from '../../services/api/JuniorApi'

import { sproglandHeaderBackImage, sproglandHeaderStyle, sproglandJuniorHeaderTitle } from './SproglandOptions'
import MainContainer from '../../components/MainContainer/MainContainer'
import Layout from '../../constants/Layout'
import Colors from '../../constants/Colors'
import Junior from '../../components/views/Sprogland/Junior'
import { CustomText } from '../../components/text/StyledText'
import { txt } from '../../locales/i18n'
import { ShadowStyles } from '../../styles'
import PhoneInputForm from '../../components/formElements/PhoneInputForm'
import { useSelector } from 'react-redux'
import { ButtonPrimary } from '../../components/buttons/StyledButtons'
import JuniorCreateDialog from '../../components/views/Sprogland/JuniorCreateDialog'
import SproglandInfo from '../../components/views/Sprogland/SproglandInfo'
import { LanguagePicker } from '../../components/formElements/LanguagePicker'

const { width } = Dimensions.get('window')
const small = width < 800

const banner = 'https://filer.boblberg.dk/Assets/Img/sprogland/sprogland-junior-banner.png'

export default function SproglandJuniorScreen() {
	const navigation = useNavigation()

	const [juniors, setJuniors] = useState([])
	const [juniorCreateModalVisible, setJuniorCreateModalVisible] = useState(false)

	const phoneNumber = useSelector((state) => state.user?.phone)

	useEffect(() => {
		navigation.setOptions({
			headerBackTitle: small && ' ',
			headerBackTitleStyle: { color: Colors.text, fontFamily: 'raleway-regular' },
			headerBackImage: () => sproglandHeaderBackImage(navigation),
			headerTitle: () => sproglandJuniorHeaderTitle(true),
			headerStyle: { ...sproglandHeaderStyle, backgroundColor: Colors.white },
			headerRight: () => (
				<View style={{ flexDirection: 'row', alignItems: 'center' }}>
					{!small && <CustomText font="bold">{'Language:'}</CustomText>}
					<LanguagePicker onChangeStartScreen={'Sprogland'} />
				</View>
			),
		})

		getJuniorProfiles().then((juniors) => {
			setJuniors(juniors)
		})
	}, [])

	async function handleCreateJunior({ name, birthday }) {
		const junior = await createJuniorProfile({ name, birthday })
		setJuniors((prevState) => [...prevState, junior])
		return junior
	}

	async function handleUpdateJunior({ name, birthday, id }) {
		const junior = await updateJuniorProfile({ name, birthday, id })
		if (junior) {
			setJuniors((prevState) => {
				const juniors = prevState.map((j) => (j.id === junior.id ? junior : j))
				return [...juniors]
			})
		}
	}

	async function handleDeleteJunior(id) {
		const res = await deleteJuniorProfile(id)
		if (!res.error) {
			setJuniors((prevState) => {
				const juniors = prevState.filter((j) => j.id !== id)
				return [...juniors]
			})
		}
		return res
	}

	return (
		<MainContainer backgroundColor={Colors.redCrossBackground}>
			<View style={styles.container}>
				<Image
					style={[styles.bannerImage, !small && { height: 150 }]}
					resizeMode={'contain'}
					accessibilityIgnoresInvertColors
					source={{ uri: banner }}
					accessible
					accessibilityRole="image"
				/>
				<View style={!Layout.isSmallDevice && styles.element}>
					<View style={{ marginVertical: 24, marginHorizontal: 10 }}>
						<CustomText font="largeBold" style={{ marginBottom: 12 }}>
							{txt('learnDanish.junior.heading')}
						</CustomText>
						<CustomText>
							{juniors.length
								? txt('learnDanish.junior.subHeader')
								: txt('learnDanish.junior.description')}
						</CustomText>

						{!juniors.length && (
							<ButtonPrimary
								title={txt('learnDanish.junior.addProfilesButton')}
								onPress={() => setJuniorCreateModalVisible(true)}
								style={[styles.button, { alignSelf: 'start', backgroundColor: Colors.redCross.base, marginTop: 12 }]}
								titleStyle={{ color: Colors.white, fontSize: 14 }}
								icon='user-plus'
								iconSize={16}
							/>
						)}
					</View>

					{juniors.map((junior) => (
						<Junior
							key={junior.id}
							junior={junior}
							updateJunior={handleUpdateJunior}
							deleteJunior={handleDeleteJunior}
						/>
					))}
				</View>

				{juniors.length > 0 && (phoneNumber === '0' || !phoneNumber) && (
					<View style={!Layout.isSmallDevice && styles.element}>
						<CustomText font="largeBold" style={{ marginHorizontal: 10 }}>
							{'Telefonnummer'}
						</CustomText>
						<View style={{ marginVertical: 12 }}>
							<PhoneInputForm
								buttonTitleStyle={{ fontSize: 16, color: Colors.white }}
								buttonStyle={{ backgroundColor: Colors.redCross.base, borderRadius: 10 }}
								style={{ ...ShadowStyles.unsetShadows, borderRadius: 10 }}
								textStyle={{ textAlign: 'left', color: Colors.inactive }}
							/>
						</View>
					</View>
				)}

				{juniors.length > 0 && (
					<View style={[!Layout.isSmallDevice && styles.element, { marginTop: 24 }]}>
						<CustomText font="largeBold" style={{ marginHorizontal: 10 }}>
							{txt('learnDanish.junior.addProfilesTitle')}
						</CustomText>
						<CustomText style={{ marginHorizontal: 10, marginTop: 12 }}>
							{txt('learnDanish.junior.addProfilesDescription')}
						</CustomText>
						<ButtonPrimary
							title={txt('learnDanish.junior.addProfilesButton')}
							onPress={() => setJuniorCreateModalVisible(true)}
							style={[styles.button, { alignSelf: 'start', backgroundColor: Colors.redCross.base, marginTop: 12, marginHorizontal: 10 }]}
							titleStyle={{ color: Colors.white, fontSize: 14 }}
							icon='user-plus'
							iconSize={16}
						/>
					</View>
				)}

				<View style={[!Layout.isSmallDevice && styles.element, { marginTop: 50 }]}>
					<SproglandInfo junior />
				</View>

				<JuniorCreateDialog
					visible={juniorCreateModalVisible}
					onDismiss={() => setJuniorCreateModalVisible(false)}
					createJunior={handleCreateJunior}
				/>
			</View>
		</MainContainer>
	)
}

const styles = StyleSheet.create({
	container: {
		flex: 1,
		width: '100%',
		alignSelf: 'center',
		marginBottom: 50,
	},
	bannerImage: {
		alignSelf: 'center',
		width: width,
		height: 125,
		marginVertical: 24,
	},
	element: {
		minWidth: 500,
		maxWidth: 900,
		width: '100%',
		alignSelf: 'center',
	},
	button: {
		borderRadius: 10,
		backgroundColor: Colors.redCross.background,
		paddingVertical: 13,
		paddingHorizontal: Layout.small ? 24 : 32,
		minWidth: Layout.small ? 75 : 115,
	},
})
